import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './Contact.css';
import axiosInstance from '../../api/axiosInstance';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    designation: '',
    mobile: '',
    email: '',
    serviceRequired: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [successBanner, setSuccessBanner] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const servicesOptions = [
    'New Medical College Establishment',
    'MBBS Seat Expansion',
    'New PG (MD/MS) Courses',
    'PG Seat Expansion',
    'Institutional & Teaching Hospital Compliance',
    'Documentation & Institutional Readiness',
    'Medical Faculty Recruitment',
    'Other Professional Requirement',
  ];

  // 👉 10 सेकंड बाद Success Banner अपने आप गायब (Auto-Remove) होने का लॉजिक
  useEffect(() => {
    let timer;
    if (successBanner) {
      timer = setTimeout(() => {
        setSuccessBanner(false);
      }, 10000); // 10 Seconds
    }
    return () => clearTimeout(timer);
  }, [successBanner]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Mobile Input: Only allow digits and max 10 chars
    if (name === 'mobile') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        mobile: digitsOnly,
      }));
      if (errorMsg) setErrorMsg('');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Indian Mobile Number Check (10 digits starting with 6, 7, 8, 9)
    const indianMobileRegex = /^[6-9]\d{9}$/;
    if (!indianMobileRegex.test(formData.mobile.trim())) {
      setErrorMsg('Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9.');
      return;
    }

    // Email Format Check
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email.trim())) {
      setErrorMsg('Please enter a valid email address (e.g. name@domain.com).');
      return;
    }

    // Message Length Check (Min 5 chars)
    if (formData.message.trim().length < 5) {
      setErrorMsg('Message must be at least 5 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/api/contact/submit', formData);
      if (response.data?.success || response.status === 201 || response.status === 200) {
        setSuccessBanner(true);
        setErrorMsg('');
        
        // Reset form fields
        setFormData({
          name: '',
          organisation: '',
          designation: '',
          mobile: '',
          email: '',
          serviceRequired: '',
          message: '',
        });
      }
    } catch (err) {
      console.error('Submission Error:', err);
      const msg =
        err.response?.data?.message ||
        'Unable to send message. Please check your connection and try again.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Nutrivio Medical Services</title>
        <meta
          name="description"
          content="Get in touch with Nutrivio Medical Services for advisory support, medical faculty solutions, and institutional guidance."
        />
      </Helmet>

      <div className="contact-page-wrapper">
        <div className="contact-ambient-radial"></div>

        <div className="contact-main-container">
          <div className="contact-grid-layout">
            
            {/* =========================================================
                LEFT COLUMN: CORPORATE CONTACT DETAILS
            ========================================================= */}
            <div className="contact-info-column">
              
              {/* 1. Header Card */}
              <div className="contact-card-box contact-intro-card">
                <div className="contact-header-badge">
                  <span className="contact-live-dot"></span>
                  <span>Get In Touch With Nutrivio</span>
                </div>

                <h1 className="contact-main-headline">
                  Let’s Discuss Your <br />
                  <span className="contact-gradient-text">Requirements</span>
                </h1>

                <p className="contact-intro-description">
                  Whether you have a query regarding new medical colleges, academic expansion, documentation readiness, or medical faculty recruitment, our team is here to assist you.
                </p>
              </div>

              {/* 2. Direct Channels: Phone, WhatsApp & Email */}
              <div className="contact-channels-container">
                
                {/* Phone Card */}
                <a href="tel:+919038320103" className="contact-channel-item call-item">
                  <div className="contact-channel-icon-box call">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div className="contact-channel-details">
                    <span className="contact-channel-label">Official Phone</span>
                    <strong className="contact-channel-value">+91 90383 20103</strong>
                  </div>
                </a>

                {/* WhatsApp Card */}
                <a href="https://wa.me/919038320103" target="_blank" rel="noopener noreferrer" className="contact-channel-item whatsapp-item">
                  <div className="contact-channel-icon-box whatsapp">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                    </svg>
                  </div>
                  <div className="contact-channel-details">
                    <span className="contact-channel-label">WhatsApp Contact</span>
                    <strong className="contact-channel-value">+91 90383 20103</strong>
                  </div>
                </a>

                {/* Email Card */}
                <a href="mailto:nutrivioheadoffice@gmail.com" className="contact-channel-item email-item">
                  <div className="contact-channel-icon-box email">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div className="contact-channel-details">
                    <span className="contact-channel-label">Official Email</span>
                    <strong className="contact-channel-value text-break">nutrivioheadoffice@gmail.com</strong>
                  </div>
                </a>

              </div>

              {/* 3. Office & Timing Card */}
              <div className="contact-card-box contact-office-card">
                <div className="contact-office-header">
                  <div className="contact-office-icon-wrap">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
                    </svg>
                  </div>
                  <div>
                    <h6>Registered & Corporate Office</h6>
                    <small>Mani Casadona, IT/ITES Hub, New Town, Rajarhat, Kolkata – 700161</small>
                  </div>
                </div>

                <div className="contact-office-meta-row">
                  <div className="contact-meta-pill">
                    <span className="contact-meta-dot"></span>
                    <span>Mon – Sat: 09:30 AM – 06:30 PM IST</span>
                  </div>
                  <div className="contact-meta-pill">
                    <span className="contact-meta-dot green"></span>
                    <span>Pan-India Support</span>
                  </div>
                </div>
              </div>

            </div>

            {/* =========================================================
                RIGHT COLUMN: CONTACT US FORM
            ========================================================= */}
            <div className="contact-form-column">
              <div className="contact-form-card">
                
                <div className="contact-form-header">
                  <div className="contact-form-icon-bubble">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="contact-form-title">Contact Us</h3>
                    <p className="contact-form-sub">Please fill in your details and message below.</p>
                  </div>
                </div>

                {/* 👉 ONE-LINE SUCCESS BANNER (With ✕ Cross & 10s Auto-Remove) */}
                {successBanner && (
                  <div className="contact-one-line-success-banner" role="alert">
                    <div className="contact-banner-content-left">
                      <span className="contact-banner-success-check">✓</span>
                      <span className="contact-banner-text">
                        Message sent successfully! Our team will get back to you shortly.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="contact-banner-close-btn"
                      onClick={() => setSuccessBanner(false)}
                      aria-label="Close message"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* 👉 Dynamic Error Banner */}
                {errorMsg && (
                  <div className="contact-one-line-error-banner" role="alert">
                    <div className="contact-banner-content-left">
                      <span>⚠️</span>
                      <span className="contact-banner-text">{errorMsg}</span>
                    </div>
                    <button
                      type="button"
                      className="contact-banner-close-btn error"
                      onClick={() => setErrorMsg('')}
                      aria-label="Close error"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Form Elements with HTML5 'required' Attributes */}
                <form className="contact-form-element" onSubmit={handleSubmit}>
                  
                  {/* Field: Full Name */}
                  <div className="contact-form-group">
                    <label className="contact-form-label">
                      Full Name <span className="contact-req">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="contact-form-input"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Field Row: Organisation & Designation */}
                  <div className="contact-form-row">
                    <div className="contact-form-group">
                      <label className="contact-form-label">
                        Organisation <span className="contact-req">*</span>
                      </label>
                      <input
                        type="text"
                        name="organisation"
                        className="contact-form-input"
                        placeholder="Enter organisation name"
                        value={formData.organisation}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="contact-form-group">
                      <label className="contact-form-label">
                        Designation <span className="contact-req">*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        className="contact-form-input"
                        placeholder="Enter your designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field Row: Mobile & Email */}
                  <div className="contact-form-row">
                    <div className="contact-form-group">
                      <label className="contact-form-label">
                        Mobile Number <span className="contact-req">*</span>
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        className="contact-form-input"
                        placeholder="Enter phone number"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        maxLength={10}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="contact-form-group">
                      <label className="contact-form-label">
                        Official Email <span className="contact-req">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="contact-form-input"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field: Service Required */}
                  <div className="contact-form-group">
                    <label className="contact-form-label">
                      Service Category Required <span className="contact-req">*</span>
                    </label>
                    <select
                      name="serviceRequired"
                      className="contact-form-select"
                      value={formData.serviceRequired}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select a service</option>
                      {servicesOptions.map((opt, i) => (
                        <option key={i} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Field: Message */}
                  <div className="contact-form-group">
                    <label className="contact-form-label">
                      Message <span className="contact-req">*</span>
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      className="contact-form-textarea"
                      placeholder="Write here..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <span>SENDING MESSAGE...</span>
                    ) : (
                      <>
                        <span>SEND MESSAGE</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="contact-confidential-note">
                    🔒 Confidential Communication. Your information remains strictly protected.
                  </p>

                </form>

              </div>
            </div>

          </div>

          {/* BOTTOM SECTION: GOOGLE MAP */}
          <div className="contact-map-wrapper">
            <div className="contact-map-card">
              <div className="contact-map-header">
                <div className="contact-map-title-left">
                  <div className="contact-map-pin">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <h6>Mani Casadona Business Hub, New Town, Kolkata</h6>
                    <small>Corporate Office Location</small>
                  </div>
                </div>
                <span className="contact-map-corp-badge">Nutrivio HQ</span>
              </div>

              <div className="contact-map-frame-box">
                <iframe
                  title="Nutrivio Head Office Location Map"
                  src="https://maps.google.com/maps?q=Mani%20Casadona%2C%20Action%20Area%20IIF%2C%20New%20Town%2C%20Kolkata%2C%20West%20Bengal%20700161&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="340"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Contact;